/* -------------------------- Design imports start -------------------------- */
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material'
import { Box, Grid, IconButton, Modal, Stack, Typography } from '@mui/material'
import { Timeline } from 'antd'

/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import Button from '../../../components/Button'
import LogTool from '../../../logger/logTools'
import { fetchVersionComparison, revertObjectToVersion } from '../api/versionApi'
import { type Version, type VersionComparison } from '../utils/types'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  versions: Version[]
  formState: {
    state: any | undefined
    setState: React.Dispatch<React.SetStateAction<any | undefined>>
  }
  setOpen: (open: boolean) => void
  setVersionDifferences: React.Dispatch<React.SetStateAction<VersionComparison | undefined>>
  setCompareOpen: React.Dispatch<React.SetStateAction<boolean>>
  onRevertVersion: (version: Version) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function VersionTimeline(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { versions, formState, setOpen, setVersionDifferences, setCompareOpen } = props
  const { t } = useTranslation(['common', 'version'])
  const log = new LogTool({ context: 'VersionTimeline', enable: true, logLevel: 'warn' })
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [revertModalOpen, setRevertModalOpen] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [showVersionData, setShowVersionData] = useState<number>(-1)

  const [versionToRevert, setVersionToRevert] = useState<Version | undefined>(undefined)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */

  const handleRevertVersion = async () => {
    if (!versionToRevert || !formState.state?.self) {
      return
    }

    try {
      await revertObjectToVersion(formState.state.self, versionToRevert)
      toast.success(t('common:feedback.success.revertedToVersionSuccessfully'))
      props.onRevertVersion(versionToRevert)
    } catch (error) {
      toast.error(t('common:feedback.error.revertedToVersionSuccessfully'))
      log.error(error)
    }

    setRevertModalOpen(false)
    setOpen(false)
  }

  const handleFetchVersionDifferences = async (
    object_self: string,
    version1: string,
    version2: string
  ) => {
    try {
      const comparison = await fetchVersionComparison(object_self, version1, version2)
      setVersionDifferences(comparison)
    } catch (error) {
      log.error(error)
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */

  const timelineItems = versions.map((version: Version, index: number) => {
    return {
      key: index,
      color: "#16a9e4",
      children: (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ flex: 1 }}>{version.comment}</Typography>
            <IconButton
              id="expandVersionButton"
              aria-label="expand"
              size="small"
              onClick={() =>
                showVersionData === index ? setShowVersionData(-1) : setShowVersionData(index)
              }
            >
              {showVersionData === index ? (
                <KeyboardArrowUpOutlined />
              ) : (
                <KeyboardArrowDownOutlined />
              )}
            </IconButton>
          </Box>
          {showVersionData === index && (
            <Stack spacing={2}>
              <p>
                {t('common:content.label.user')}: {version.user} <br />
                {t('common:content.label.date')}:{' '}
                {moment(version.created_date).format('MMMM Do YYYY, h:mm:ss a')} <br />
              </p>
              {index !== 0 && (
                <Stack spacing={1}>
                  <Button
                    id="revertVersionButton"
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setVersionToRevert(version)
                      setRevertModalOpen(true)
                    }}
                  >
                    {t('common:interaction.button.revertToThisVersion')}
                  </Button>
                  <Button
                    id="compareVersionButton"
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (formState.state?.self) {
                        void (async () => {
                          await handleFetchVersionDifferences(
                            formState.state.self,
                            version.version_id,
                            versions[0].version_id
                          )
                        })()
                        setCompareOpen(true)
                      }
                    }}
                  >
                    {t('common:interaction.button.compareWithCurrentVersion')}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </>
      )
    }
  })

  const revertModal = () => {
    return (
      <Modal
        open={revertModalOpen}
        onClose={() => setRevertModalOpen(false)}
        aria-labelledby="revert-modal"
        aria-describedby="modal-for-reverting-version"
      >
        <Grid
          container
          spacing={2}
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
            <Typography variant="h6">{t('common:content.label.revertVersion')}</Typography>
          </Grid>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
            <Typography>{t('common:feedback.info.areYouSureVersion')}</Typography>
          </Grid>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
            <Button
              id="cancelRevertButton"
              variant="outlined"
              color="inherit"
              sx={{ float: 'left' }}
              onClick={() => setRevertModalOpen(false)}
            >
              {t('common:interaction.button.cancel')}
            </Button>
            <Button
              id="revertButton"
              variant="contained"
              sx={{ float: 'right' }}
              onClick={handleRevertVersion}
            >
              {t('common:interaction.button.revert')}
            </Button>
          </Grid>
        </Grid>
      </Modal>
    )
  }
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */

  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <Timeline mode={'left'} items={timelineItems} />
      {revertModal()}
    </>
  )
}
